import { sql } from "drizzle-orm";
import {
  type AnyPgColumn,
  boolean,
  check,
  index,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import { organisations, students, users } from "./schema.js";

const id = () =>
  varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`);

const organisationId = () =>
  varchar("organisation_id")
    .notNull()
    .references(() => organisations.id, { onDelete: "restrict" });

/**
 * A check constraint built from the exported vocabulary rather than beside it.
 *
 * The list and the constraint drift apart the moment they are written twice,
 * and the failure is quiet: application code accepts a value the database then
 * refuses, or worse, the constraint keeps allowing a value nothing produces
 * any more. The literals are our own, never anything a caller supplied.
 */
function oneOf(column: AnyPgColumn, values: readonly string[]) {
  return sql`${column} in (${sql.raw(values.map((value) => `'${value}'`).join(", "))})`;
}

/**
 * What a conversation is *for*, which is not the same as who is in it.
 *
 * `student_family` is the tutor/office ↔ family conversation about one child.
 * `staff_direct` is colleague to colleague. `broadcast` is one message to a
 * resolved audience that does not become a group chat. `system` is generated
 * correspondence — a waitlist decision, say — that a person may want to return
 * to later, as against a bell notification, which is only awareness.
 */
export const COMMUNICATION_THREAD_KINDS = [
  "student_family",
  "staff_direct",
  "broadcast",
  "system",
] as const;
export type CommunicationThreadKind = (typeof COMMUNICATION_THREAD_KINDS)[number];

/**
 * Who may add to a conversation, decided when it is created rather than
 * inferred from its kind at read time.
 *
 * `participants` is an ordinary conversation. `private_reply` is a reply that
 * goes only to the staff side, so one family's answer never reaches another.
 * `none` is an announcement: durable, readable, and not a thing to answer.
 */
export const COMMUNICATION_REPLY_POLICIES = ["participants", "private_reply", "none"] as const;
export type CommunicationReplyPolicy = (typeof COMMUNICATION_REPLY_POLICIES)[number];

/**
 * What produced one message.
 *
 * `human` is somebody typing. `system` is generated correspondence with an
 * author of null. `announcement` is an owner broadcast. The column was
 * `staff_message` for every row before this vocabulary existed; migration 0055
 * rewrites those to `human`, because two vocabularies in one column is how a
 * classification stops being able to answer anything.
 */
export const COMMUNICATION_MESSAGE_KINDS = ["human", "system", "announcement"] as const;
export type CommunicationMessageKind = (typeof COMMUNICATION_MESSAGE_KINDS)[number];

/**
 * The capacity somebody joined a conversation in, not their job title.
 *
 * A role can change — a parent's account can later be given a tutor profile,
 * and a tutor leaves — but why they are in *this* conversation does not. The
 * two are separate facts and this is the one the conversation depends on.
 */
export const COMMUNICATION_PARTICIPANT_ROLES = ["staff", "family"] as const;
export type CommunicationParticipantRole = (typeof COMMUNICATION_PARTICIPANT_ROLES)[number];

export const COMMUNICATION_IMPORTANCE = ["normal", "important"] as const;
export type CommunicationImportance = (typeof COMMUNICATION_IMPORTANCE)[number];

/**
 * Who an owner's announcement was addressed to, recorded rather than inferred.
 *
 * The frozen recipient rows say *which accounts* received it, and they are the
 * authority for every read. This says what the owner asked for, which is a
 * different fact and the only one the Sent view can honestly report: an
 * announcement to all families that reached 142 accounts is not the same
 * statement as one addressed to 142 accounts by hand, and counting rows cannot
 * tell them apart afterwards.
 *
 * Null for everything that is not a broadcast. "One family" is deliberately not
 * here — that is an ordinary conversation with that family, not an
 * announcement, and it goes through the same thread the student page opens.
 */
export const COMMUNICATION_AUDIENCES = ["all_families", "all_staff"] as const;
export type CommunicationAudience = (typeof COMMUNICATION_AUDIENCES)[number];

/**
 * The name a standing conversation is found by, rather than created twice.
 *
 * Two tutors opening the same family's conversation at the same moment is
 * exactly the case a read-then-insert loses, so the identity is a value with a
 * unique index on it rather than a query somebody hopes wins the race. Null for
 * a conversation that is genuinely one of a kind — an announcement, or one of
 * the historical single-message threads — because null does not collide.
 *
 * **One conversation per child *per member of staff*.** Not one per child. A
 * frank remark about a family situation is written for the person who needs
 * it, and the colleague teaching the same child a different instrument was not
 * who the writer had in mind — the same rule `canReadLessonNote` already
 * applies to lesson notes, and it would be strange for messages to be laxer
 * than notes about the same lesson.
 */
export function studentFamilyThreadKey(studentId: string, staffAccountId: string): string {
  return `student_family:${studentId}:${staffAccountId}`;
}

/**
 * Sorted, so whichever colleague opens it first gets the same conversation.
 */
export function staffDirectThreadKey(accountIdA: string, accountIdB: string): string {
  return `staff_direct:${[accountIdA, accountIdB].sort().join(":")}`;
}

/**
 * A conversation: the thing a person opens, as against a message they read.
 *
 * The thread carries who belongs to the conversation and what may be done in
 * it. It deliberately carries no audience of its own — `communication_recipients`
 * below is still the frozen, per-message record of who a given message went to,
 * and every read is driven by that rather than by thread membership. That split
 * is what lets a broadcast to two hundred accounts exist without two hundred
 * participant rows, and what stops somebody added to a conversation tomorrow
 * from being able to read what was said in it yesterday.
 */
export type CommunicationAnnouncementTarget =
  | {
      kind: "tutor_service";
      tutorProfileId: string;
      scopeKind: "service_id";
      scopeValue: number;
    }
  | {
      kind: "tutor_service";
      tutorProfileId: string;
      scopeKind: "service_name" | "instrument";
      scopeValue: string;
    }
  | { kind: "appointment"; appointmentId: string };

export type CommunicationAnnouncementFilter =
  | (Extract<CommunicationAnnouncementTarget, { kind: "tutor_service" }> & { criteria: string })
  | (Extract<CommunicationAnnouncementTarget, { kind: "appointment" }> & {
      criteria: string;
      startsAt?: string;
    });

export const communicationThreads = pgTable(
  "communication_threads",
  {
    id: id(),
    organisationId: organisationId(),
    kind: text("kind").notNull(),
    /** The child this conversation is about, or null for staff and org-wide threads. */
    studentId: varchar("student_id").references(() => students.id, { onDelete: "cascade" }),
    subject: text("subject").notNull(),
    /** Null for a system-created thread, and for one whose author's account is gone. */
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    replyPolicy: text("reply_policy").notNull().default("participants"),
    /**
     * What a standing conversation is found by. See `studentFamilyThreadKey`
     * and `staffDirectThreadKey`; null for a one-off.
     */
    identityKey: text("identity_key"),
    /** What an owner asked for, for a broadcast. Null for everything else. */
    audience: text("audience"),
    /** The server-resolved private filter frozen with an announcement. */
    announcementFilter: jsonb("announcement_filter").$type<CommunicationAnnouncementFilter>(),
    /**
     * What a generated conversation is about, so authority over it can be
     * proved from the thing itself.
     *
     * A waitlist request's thread is attached to the child, but the tutor a
     * family has *asked for* has no teaching authority over that child yet —
     * asking for it is the point of the request. Without this the message
     * telling them about it was one they could not open.
     */
    contextType: text("context_type"),
    contextId: varchar("context_id"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    /**
     * What the Inbox orders by, and the only sense in which a thread is ever
     * "updated" — the row itself is otherwise immutable. A general `updated_at`
     * would answer a question nothing asks and would move for reasons the
     * ordering must not follow.
     */
    lastMessageAt: timestamp("last_message_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("communication_threads_org_recent_idx").on(t.organisationId, t.lastMessageAt, t.id),
    index("communication_threads_student_idx").on(t.organisationId, t.studentId),
    // Null keys do not collide in Postgres, so standing conversations are
    // constrained and one-off ones are left alone.
    uniqueIndex("communication_threads_identity_idx").on(t.organisationId, t.identityKey),
    index("communication_threads_context_idx").on(t.organisationId, t.contextType, t.contextId),
    check("communication_threads_kind_check", oneOf(t.kind, COMMUNICATION_THREAD_KINDS)),
    check(
      "communication_threads_reply_policy_check",
      oneOf(t.replyPolicy, COMMUNICATION_REPLY_POLICIES),
    ),
    check(
      "communication_threads_audience_check",
      sql`${t.audience} is null or ${oneOf(t.audience, COMMUNICATION_AUDIENCES)}`,
    ),
  ],
);

/**
 * Who belongs to a conversation now.
 *
 * Membership, not audience. It answers "may this account open this thread and
 * add to it", which is a live question; the frozen answer to "who was this
 * message sent to" stays on the recipient rows. `leftAt` keeps a departure
 * legible without deleting the row, so a thread somebody has left is still
 * attributable and still readable to them where the message-level snapshot
 * says it should be.
 */
export const communicationThreadParticipants = pgTable(
  "communication_thread_participants",
  {
    id: id(),
    organisationId: organisationId(),
    threadId: varchar("thread_id")
      .notNull()
      .references(() => communicationThreads.id, { onDelete: "cascade" }),
    accountId: varchar("account_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    participantRole: text("participant_role").notNull(),
    joinedAt: timestamp("joined_at", { withTimezone: true }).notNull().defaultNow(),
    leftAt: timestamp("left_at", { withTimezone: true }),
  },
  (t) => [
    uniqueIndex("communication_thread_participants_thread_account_idx").on(
      t.threadId,
      t.accountId,
    ),
    index("communication_thread_participants_account_idx").on(
      t.organisationId,
      t.accountId,
      t.leftAt,
    ),
    check(
      "communication_thread_participants_role_check",
      oneOf(t.participantRole, COMMUNICATION_PARTICIPANT_ROLES),
    ),
  ],
);

/**
 * The authoritative, immutable copy of a communication.
 *
 * Email is intentionally not represented as the message itself. Delivery
 * metadata belongs on each resolved recipient, so a provider failure can never
 * make the Hub copy disappear or change who the message was originally for.
 */
export const communications = pgTable(
  "communications",
  {
    id: id(),
    organisationId: organisationId(),
    threadId: varchar("thread_id")
      .notNull()
      .references(() => communicationThreads.id, { onDelete: "cascade" }),
    /** Null for a staff or organisation-wide message. */
    studentId: varchar("student_id").references(() => students.id, { onDelete: "cascade" }),
    kind: text("kind").notNull().default("human"),
    subject: text("subject").notNull(),
    body: text("body").notNull(),
    /** Null for a generated message, and for one whose author's account is gone. */
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    importance: text("importance").notNull().default("normal"),
    /**
     * What a generated message is *about*, so the thing it refers to can be
     * found from the message and the message from the thing. Free text rather
     * than an enum: the vocabulary belongs to whichever feature generates the
     * message, and a new one should not need a migration.
     */
    contextType: text("context_type"),
    contextId: varchar("context_id"),
    /**
     * What stops a retry, a double click or a second scheduler publishing the
     * same generated message twice. Named by the caller after the thing and the
     * transition it represents — `waitlist-request-approved:<id>:<account>` —
     * and refused by the unique index below. Null for anything a person typed,
     * which is genuinely repeatable: somebody may send the same words twice.
     *
     * The same shape as `email_messages.dedupe_key`, deliberately. Two
     * different idempotency mechanisms is one more than anybody can hold in
     * their head when a duplicate does get through.
     */
    dedupeKey: text("dedupe_key"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    publishedAt: timestamp("published_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("communications_student_published_idx").on(
      t.organisationId,
      t.studentId,
      t.publishedAt,
      t.id,
    ),
    index("communications_thread_published_idx").on(t.threadId, t.publishedAt, t.id),
    // Null keys do not collide in Postgres, so generated messages are
    // constrained and typed ones are left alone.
    uniqueIndex("communications_dedupe_idx").on(t.organisationId, t.dedupeKey),
    check("communications_importance_check", oneOf(t.importance, COMMUNICATION_IMPORTANCE)),
    check("communications_kind_check", oneOf(t.kind, COMMUNICATION_MESSAGE_KINDS)),
  ],
);

/**
 * Publication-time recipient snapshot.
 *
 * A relationship changing tomorrow does not rewrite yesterday's recorded
 * audience, but live family access is still checked when the message is read.
 * Parent and child accounts therefore have separate rows and separate readAt
 * timestamps even when their sign-in/recovery addresses happen to be shared.
 */
export const communicationRecipients = pgTable(
  "communication_recipients",
  {
    id: id(),
    organisationId: organisationId(),
    communicationId: varchar("communication_id")
      .notNull()
      .references(() => communications.id, { onDelete: "cascade" }),
    accountId: varchar("account_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    /** Null for a staff or organisation-wide message. */
    studentId: varchar("student_id").references(() => students.id, { onDelete: "cascade" }),
    readAt: timestamp("read_at", { withTimezone: true }),
    emailRequested: boolean("email_requested").notNull().default(false),
    /** Outbox id once optional email delivery is wired in a later rollout. */
    emailMessageId: varchar("email_message_id"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("communication_recipients_message_account_idx").on(
      t.communicationId,
      t.accountId,
    ),
    index("communication_recipients_account_unread_idx").on(
      t.organisationId,
      t.accountId,
      t.studentId,
      t.readAt,
    ),
  ],
);
